import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Lock, Mail, User, Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import TrialezeLogo from "@/components/shared/TrialezeLogo";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, loginWithGoogle, user } = useAuth();
  const [form, setForm] = useState({ 
    email: '', 
    password: '', 
    confirmPassword: '',
    name: '', 
    firm_name: '' 
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/platform/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await register({
        email: form.email,
        password: form.password,
        name: form.name,
        firm_name: form.firm_name
      });
      navigate('/platform/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(180deg, #f0f7ff 0%, #ffffff 30%, #f8fbff 100%)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <TrialezeLogo size="large" />
          </Link>
          <h1 className="text-2xl font-bold mt-4" style={{ color: '#1E293B' }}>Create Account</h1>
          <p className="text-sm mt-2" style={{ color: '#475569' }}>Join the invitation-only legal intelligence platform</p>
        </div>
        
        <div className="rounded-3xl p-8" style={{ 
          background: 'rgba(255,255,255,.98)', 
          border: '1px solid #D9E4F2',
          boxShadow: '0 12px 30px rgba(15,23,42,.08)'
        }}>
          {error && (
            <Alert className="mb-6 border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700">{error}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm" style={{ color: '#1E293B' }}>Full Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#94A3B8' }} />
                <Input 
                  type="text"
                  placeholder="John Smith"
                  className="pl-10 rounded-xl h-11"
                  style={{ background: '#F8FAFC', border: '1px solid #D9E4F2' }}
                  value={form.name}
                  onChange={e => setForm(f => ({...f, name: e.target.value}))}
                  required
                  data-testid="register-name"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm" style={{ color: '#1E293B' }}>Firm Name</Label>
              <div className="relative mt-1">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#94A3B8' }} />
                <Input 
                  type="text"
                  placeholder="Smith & Associates LLP"
                  className="pl-10 rounded-xl h-11"
                  style={{ background: '#F8FAFC', border: '1px solid #D9E4F2' }}
                  value={form.firm_name}
                  onChange={e => setForm(f => ({...f, firm_name: e.target.value}))}
                  data-testid="register-firm"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm" style={{ color: '#1E293B' }}>Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#94A3B8' }} />
                <Input 
                  type="email"
                  placeholder="you@lawfirm.com"
                  className="pl-10 rounded-xl h-11"
                  style={{ background: '#F8FAFC', border: '1px solid #D9E4F2' }}
                  value={form.email}
                  onChange={e => setForm(f => ({...f, email: e.target.value}))}
                  required
                  data-testid="register-email"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm" style={{ color: '#1E293B' }}>Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#94A3B8' }} />
                <Input 
                  type="password"
                  placeholder="Minimum 8 characters"
                  className="pl-10 rounded-xl h-11"
                  style={{ background: '#F8FAFC', border: '1px solid #D9E4F2' }}
                  value={form.password}
                  onChange={e => setForm(f => ({...f, password: e.target.value}))}
                  required
                  data-testid="register-password"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm" style={{ color: '#1E293B' }}>Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#94A3B8' }} />
                <Input 
                  type="password"
                  placeholder="Confirm your password"
                  className="pl-10 rounded-xl h-11"
                  style={{ background: '#F8FAFC', border: '1px solid #D9E4F2' }}
                  value={form.confirmPassword}
                  onChange={e => setForm(f => ({...f, confirmPassword: e.target.value}))}
                  required
                  data-testid="register-confirm-password"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full rounded-xl h-12 font-bold text-white"
              style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)' }}
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Create Account'}
            </Button>
          </form>
          
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: '#D9E4F2' }}></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-2" style={{ background: '#fff', color: '#94A3B8' }}>Or continue with</span>
            </div>
          </div>
          
          <Button 
            type="button"
            variant="outline"
            className="w-full rounded-xl h-12 font-semibold border-slate-200 hover:bg-slate-50"
            onClick={loginWithGoogle}
            data-testid="google-register"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </Button>
          
          <p className="text-center text-sm mt-6" style={{ color: '#475569' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-semibold hover:underline" style={{ color: '#2563EB' }}>
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
};

export default RegisterPage;
